import { useEffect, useState } from "react";
import { getOrderActivity } from "@/lib/orderApi";

const timeOf = iso =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

/**
 * "Who did what" for one order (owner, 2026-10-31: show who was responsible for
 * every action — KOT, print bill, settle — on the PC). Each row names the
 * person the audit log names; the kitchen slip names the same person.
 *
 * `refreshKey` re-reads after the screen changes the order; `onKots` hands the
 * per-KOT sender map to a screen that labels its KOT groups.
 */
export default function OrderActivity({ orderId, token, refreshKey, onKots, className = "" }) {
  const [state, setState] = useState({ phase: "loading", rows: [] });

  useEffect(() => {
    if (!orderId) return undefined;
    let live = true;
    Promise.resolve().then(() => getOrderActivity(orderId, token))
      .then(data => {
        if (!live) return;
        const kots = data?.kots || [];
        onKots?.(Object.fromEntries(kots.map(k => [k.kot_number, k])));
        const rows = [
          ...(data?.events || []).map((e, i) => ({ key: `e-${i}-${e.action}`, label: e.label, by: e.by, at: e.at })),
          ...kots.map(k => ({ key: `k-${k.kot_number}`, label: `KOT ${k.kot_number}`, by: k.by, at: k.at })),
        ].sort((a, b) => String(a.at).localeCompare(String(b.at)));
        setState({ phase: "ready", rows });
      })
      .catch(error => {
        if (!live) return;
        const status = error?.response?.status;
        setState({ phase: "error", rows: [], message: status ? `the server answered ${status}` : "the server could not be reached" });
      });
    return () => { live = false; };
    // onKots is a setter from the parent; re-reading on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, token, refreshKey]);

  if (!orderId) return null;
  return (
    <section className={`text-meta ${className}`} data-testid="order-activity">
      <h4 className="text-micro font-bold uppercase text-muted-foreground mb-1">Who did what</h4>
      {state.phase === "error" && (
        <p className="text-danger" data-testid="order-activity-error">Could not read who did what: {state.message}.</p>
      )}
      {state.phase === "ready" && !state.rows.length && <p className="text-muted-foreground">Nothing recorded yet.</p>}
      {state.phase === "ready" && state.rows.length > 0 && (
        <ul className="flex flex-col gap-0.5">
          {state.rows.map(r => (
            <li key={r.key} className="flex justify-between gap-2" data-testid="order-activity-row">
              <span className="text-foreground">{r.label} · <strong className="font-semibold">{r.by || "unknown"}</strong></span>
              <span className="text-muted-foreground tabular-nums shrink-0">{timeOf(r.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
